import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    AUDIT_EXPORT_TOO_LARGE,
    type AuditFilterChange,
    type AuditLogEntry,
    AuditLogExportError,
    type AuditScopeOption,
    scopeOptionsFor,
    useAuditFacets,
    useAuditLog,
    useExportAuditLog,
} from '@/domain/auditLog';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import AuditLogView from './AuditLogView';
import type { AuditFilterBindings } from './filters/filterBindings';
import { useAuditColumnVisibility } from './useAuditColumnVisibility';
import { useAuditLogFilterState } from './useAuditLogFilterState';

// Enough to name every scope a reviewer picks in one sitting.
const REMEMBERED_SCOPES = 20;

function sameScope(a: AuditScopeOption, b: AuditScopeOption): boolean {
    return a.kind === b.kind && a.id === b.id;
}

export default function AuditLogContainer() {
    const { t, i18n } = useTranslation();
    const filterState = useAuditLogFilterState();
    const { filters } = filterState;
    const [selected, setSelected] = useState<AuditLogEntry | null>(null);
    const [exportError, setExportError] = useState<string | null>(null);
    const [pickedScopes, setPickedScopes] = useState<AuditScopeOption[]>([]);
    const auditLog = useAuditLog(filters);
    const { facets } = useAuditFacets();
    const exportMutation = useExportAuditLog();
    const columnVisibility = useAuditColumnVisibility();

    const rememberScope = useCallback((option: AuditScopeOption) => {
        setPickedScopes((current) =>
            [option, ...current.filter((kept) => !sameScope(kept, option))].slice(
                0,
                REMEMBERED_SCOPES
            )
        );
    }, []);

    // A click on a path segment or a dialog row comes from a loaded row, which names the
    // scope; kept like a pick, the name stays on the toolbar when the list then empties.
    const { applyChange } = filterState;
    const applyClickedFilter = useCallback(
        (change: AuditFilterChange) => {
            for (const option of scopeOptionsFor(change.filters, auditLog.entries)) {
                rememberScope(option);
            }
            applyChange(change);
        },
        [applyChange, auditLog.entries, rememberScope]
    );

    const filterBindings = useMemo(
        (): AuditFilterBindings => ({
            filters,
            onChange: filterState.setFilters,
            onType: filterState.typeFilters,
            facets,
            labelSources: { entries: auditLog.entries, scopes: pickedScopes },
            rememberScope,
        }),
        [
            filters,
            filterState.setFilters,
            filterState.typeFilters,
            facets,
            auditLog.entries,
            pickedScopes,
            rememberScope,
        ]
    );

    async function handleExport() {
        setExportError(null);
        try {
            // The URL's filters, the default view exclusion included, so the file holds
            // exactly the entries the page lists.
            await exportMutation.mutateAsync(filters);
        } catch (error) {
            // Backend and transport messages are English and often technical, so the
            // page only ever shows its own translated text.
            setExportError(
                error instanceof AuditLogExportError && error.code === AUDIT_EXPORT_TOO_LARGE
                    ? t('auditLog.page.exportTooLarge', {
                          limit: formatNumberByLocale(error.limit, parseLocale(i18n.language), 0),
                      })
                    : t('auditLog.page.exportFailed')
            );
        }
    }

    return (
        <AuditLogView
            entries={auditLog.entries}
            isLoading={auditLog.isLoading}
            error={auditLog.error}
            hasNextPage={auditLog.hasNextPage}
            isFetchingNextPage={auditLog.isFetchingNextPage}
            onLoadMore={() => {
                auditLog.fetchNextPage();
            }}
            onRefresh={() => {
                auditLog.refetch();
            }}
            isRefreshing={auditLog.isRefreshing}
            filterBindings={filterBindings}
            onFilter={applyClickedFilter}
            columnVisibility={columnVisibility.visibility}
            columns={columnVisibility.columns}
            onColumnsChange={columnVisibility.setVisibleColumns}
            onExport={handleExport}
            isExporting={exportMutation.isPending}
            exportError={exportError}
            selected={selected}
            onSelect={setSelected}
        />
    );
}
